/**
 * 展示层格式化。
 *
 * 金额在数据库里一律是整数分，到这里才转成带货币符号的字符串。
 * 用 Intl 而不是手拼 "$" —— 中英文环境下的千分位、符号位置都不一样。
 */
export function formatMoney(cents: number, locale: string, currency = 'USD'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
}

/** 分钟转小时，保留两位。工时展示统一走这个。 */
export function formatHours(minutes: number, decimals = 2): string {
  return (minutes / 60).toFixed(decimals);
}

/**
 * 分钟转「X 小时 Y 分」的两个数，文案由调用方按 i18n 拼。
 *
 * 工时表上「48.60 小时」不如「48 小时 36 分」好读 —— 后者能直接和
 * 工人自己记的时间对上。小数形式（`formatHours`）留给导出的表格。
 */
export function splitHours(minutes: number): { hours: number; minutes: number } {
  const total = Math.max(0, Math.round(minutes));
  return { hours: Math.floor(total / 60), minutes: total % 60 };
}

/**
 * 文件大小。
 *
 * 小于 1MB 的走 KB —— 写死 MB 一位小数的话，一张 30KB 的图纸显示成
 * 「0.0 MB」，看着像文件坏了。工程图动辄上百兆，KB / MB 两档就够，
 * 不做 GB：单个文件上限 300MB（CR-023）。
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * 打卡时刻，按工地时区显示。中文界面 24 小时制（17:05），英文界面 12 小时制（5:05 PM），
 * 西班牙语界面和中文一样 24 小时制（CR-055）。
 * 页面上的打卡时刻都走这一个，别再各自 `toLocaleTimeString` —— 同一张卡在两处写成两种样子，人会以为是两张。
 */
export function formatClock(at: string | Date, timeZone: string, locale: string): string {
  // 12 小时制只给英文：中文、西班牙语都习惯 24 小时
  const h12 = locale.startsWith('en');
  return new Date(at).toLocaleTimeString(locale, {
    timeZone,
    hour: h12 ? 'numeric' : '2-digit',
    minute: '2-digit',
    hour12: h12,
  });
}

/** 墙上时间 `HH:MM`（表单里的值、「他平时 17:05」）按界面语言显示 */
export function formatWallClock(hhmm: string, locale: string): string {
  const [hours, minutes] = hhmm.split(':').map(Number);
  return formatClock(new Date(Date.UTC(2000, 0, 1, hours ?? 0, minutes ?? 0)), 'UTC', locale);
}
